import React, { useEffect, useState } from "react";
import Sidebar from "../../components/Sidebar/Sidebar";
import CalendarComponent from "../../components/calendar/calendar.jsx";
import { Calendar } from "lucide-react";
import Footer from '../../components/Footer/Footer';
import { createEvent, fetchUserEvents, updateEvent, deleteEvent, fetchUpcomingEvents } from "../../components/calendar/calendarAPI.js";
import AppointmentDetailsModal from "../../components/appointments/AppointmentDetailsModal.js";

export default function Appointments() {
  const [appointments, setAppointments] = useState([]);
  const [events, setEvents] = useState([]);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [form, setForm] = useState({
    title: "",
    type: "",
    date: "",
    startTime: "",
    endTime: "",
    notes: "",
    location: "",
    reminderEnabled: false,
    reminderInterval: 60, // default to 1 hour before
  });

  useEffect(() => {
    const load = async () => {
      const userId = localStorage.getItem("userId");

      if (!userId) {
        return;
      }

      const data = await fetchUserEvents(userId);
      const data2 = await fetchUpcomingEvents(userId);
      setAppointments(data2);
      const formatted = data.map((ev) => ({
        id: ev.id,
        title: ev.event_title,
        start: ev.start_time,
        end: ev.end_time,
        extendedProps: {
          location: ev.location,
          notes: ev.notes,
          type: ev.type,
          date: ev.date,
          reminder_interval: ev.reminder_interval,
          reminder_enabled: ev.reminder_enabled,
        },
      }));
      setEvents(formatted);
    };

    load();
  }, []);

  // Merges a date (YYYY-MM-DD) with a time (HH:mm:ss or from Date)
  const combineDateAndTime = (dateStr, timeInput) => {
    const time = new Date(timeInput);
    const hours = String(time.getHours()).padStart(2, '0');
    const minutes = String(time.getMinutes()).padStart(2, '0');
    const seconds = String(time.getSeconds()).padStart(2, '0');
    return `${dateStr} ${hours}:${minutes}:${seconds}`;
  };

  const toMySQLDate = (input) => input.slice(0, 10);

  const pad = (n) => n.toString().padStart(2, "0");

  function formatToMySQLDatetime(date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  const openEditModal = (event) => {
    setEditingEvent(event);
    setShowEditModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBookAppointment = async (e) => {
    e.preventDefault();

    const { title, type, date, startTime, endTime, notes, location, reminderEnabled, reminderInterval } = form;

    if (!title || !type || !date || !startTime) {
      alert("Please fill all required fields.");
      return;
    }

    const start = `${date}T${startTime}`;
    let end = endTime ? `${date}T${endTime}` : null;

    let remindAt = null;

    if (reminderEnabled) {
      const startDate = new Date(start);
      startDate.setMinutes(startDate.getMinutes() - parseInt(reminderInterval));
      remindAt = formatToMySQLDatetime(startDate);
    }

    const userId = localStorage.getItem("userId");

    const newEvent = {
      user_id: userId,
      event_title: title,
      start_time: start,
      end_time: end || null,
      type,
      date: date,
      location: location || null,
      notes: notes || null,
      reminder_enabled: Boolean(reminderEnabled),
      reminder_interval: form.reminderEnabled ? parseInt(form.reminderInterval) : null,
      remind_at: reminderEnabled ? remindAt : null,
    };

    try {
      const createdEvent = await createEvent(newEvent);

      setEvents((prev) => [
        ...prev,
        {
          id: createdEvent.user_id,
          title: createdEvent.event_title,
          start: createdEvent.start_time,
          end: createdEvent.end_time,
          extendedProps: {
            location: createdEvent.location,
            notes: createdEvent.notes,
            type: createdEvent.type,
            date: createdEvent.date,
            reminder_interval: createdEvent.reminder_interval,
            reminder_enabled: createdEvent.reminder_enabled,
          },
        },
      ]);

      const data = await fetchUpcomingEvents(userId);
      setAppointments(data);

      setForm({
        title: "",
        type: "",
        date: "",
        startTime: "",
        endTime: "",
        notes: "",
        location: "",
        reminderEnabled: false,
        reminderInterval: 60,
      });

      setShowModal(false);
    } catch (error) {
      console.error("Error creating appointment:", error);
      alert("Failed to create appointment. Please try again.");
    }
  };


  const handleEventClick = (info) => {
    setSelectedEvent({
      id: info.event.id,
      event_title: info.event.title,
      start_time: info.event.start,
      end_time: info.event.end,
      type: info.event.extendedProps.type,
      location: info.event.extendedProps.location,
      notes: info.event.extendedProps.notes,
      date: info.event.extendedProps.date,
      reminder_interval: info.event.extendedProps.reminder_interval,
      reminder_enabled: info.event.extendedProps.reminder_enabled,
    });

    setShowDetailsModal(true);
  };

  const handleEditClick = () => {
    setShowDetailsModal(false);
    setEditingEvent({
      id: selectedEvent.id,
      event_title: selectedEvent.event_title,
      start_time: selectedEvent.start_time,
      end_time: selectedEvent.end_time,
      type: selectedEvent.type,
      location: selectedEvent.location,
      notes: selectedEvent.notes,
      date: selectedEvent.date,
      reminder_interval: selectedEvent.reminder_interval,
      reminder_enabled: selectedEvent.reminder_enabled,
    });

    setShowEditModal(true);
  };
  return (
    <div className={`ml-0 flex flex-col min-h-screen transition-all duration-300 ${sidebarExpanded ? 'md:ml-56' : 'md:ml-16'}`}>

      <Sidebar expanded={sidebarExpanded} setExpanded={setSidebarExpanded} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6 bg-gray-100">
        {/* Right Section (Appointments / Book Form) */}
        <div className="order-1 lg:order-2 space-y-6">
          <h2 className="text-2xl font-bold text-gray-700">Schedule Tests / Visits</h2>

          <div className="p-4 bg-white shadow rounded-xl">
            <h3 className="mb-4 text-lg font-bold">Upcoming Appointments</h3>

            {appointments.length === 0 ? (
              <p className="text-gray-500">No appointments found.</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {appointments.map((event) => (
                  <div
                    key={event.id}
                    className="p-4 border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition bg-white"
                    onClick={() => openEditModal(event)}
                  >
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="text-lg font-semibold text-gray-800">
                        {event.event_title}
                      </span>
                      <span
                        className={`text-sm font-medium px-2 py-0.5 rounded mb-2 ${event.type === "Doctor"
                            ? "bg-green-100 text-green-700"
                            : event.type === "Lab Test"
                              ? "bg-yellow-100 text-yellow-700"
                              : event.type === "Follow-Up"
                                ? "bg-purple-100 text-purple-700"
                                : "bg-blue-100 text-blue-700"
                          }`}
                      >
                        {event.type}
                      </span>
                    </div>

                    <div className="mt-1 text-sm text-gray-600">
                      {new Date(event.date).toLocaleDateString()} ·{" "}
                      {new Date(event.start_time).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                      {event.end_time && (
                        <>
                          {" "}
                          -{" "}
                          {new Date(event.end_time).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </>
                      )}
                    </div>

                    {(event.location || event.notes) && (
                      <div className="mt-3 border-t pt-2 text-sm text-gray-500 space-y-1">
                        {event.location && <div>{event.location}</div>}
                        {event.notes && <div className="italic">{event.notes}</div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-center mt-6">
              <button
                onClick={() => setShowModal(true)}
                className="px-6 py-3 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
              >
                Book Appointment +
              </button>
            </div>
          </div>
        </div>

        {/* Left Section (Calendar) */}
        <div className="order-2 lg:order-1 lg:col-span-2 shadow rounded-xl p-4 h-fit">
          <h2 className="text-xl font-bold mb-4">Calendar</h2>
          <CalendarComponent
            events={events}
            setEvents={setEvents}
            onEventClick={handleEventClick}
          />
        </div>
      </div>


      <Footer />

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl p-6 relative">
            {/* Close Button */}
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-800"
            >
              ✕
            </button>

            <h3 className="text-xl font-bold mb-4">Book a New Appointment</h3>

            <form className="grid grid-cols-1 md:grid-cols-2 gap-6" onSubmit={(e) => {
              e.preventDefault();
              handleBookAppointment(e);
              setShowModal(false); // close modal after submitting
            }}>
              {/* Title */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Title</label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  className="w-full p-2.5 border rounded-lg"
                  required
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  className="w-full p-2.5 border rounded-lg"
                  required
                >
                  <option value="">Select Type</option>
                  <option value="Doctor">Doctor</option>
                  <option value="Lab Test">Lab Test</option>
                  <option value="Follow-Up">Follow-Up</option>
                  <option value="Transportation">Transportation</option>
                </select>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium mb-1">Location (optional)</label>
                <input
                  type="text"
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                  className="w-full p-2.5 border rounded-lg"
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <input
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleChange}
                  className="w-full p-2.5 border rounded-lg"
                  required
                />
              </div>

              {/* Time */}
              <div className="flex gap-4 md:col-span-2">
                <div className="w-1/2">
                  <label className="block text-sm font-medium mb-1">Start Time</label>
                  <input
                    type="time"
                    name="startTime"
                    value={form.startTime}
                    onChange={handleChange}
                    className="w-full p-2.5 border rounded-lg"
                    required
                  />
                </div>
                <div className="w-1/2">
                  <label className="block text-sm font-medium mb-1">End Time (optional)</label>
                  <input
                    type="time"
                    name="endTime"
                    value={form.endTime}
                    onChange={handleChange}
                    className="w-full p-2.5 border rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Reminder</label>
                <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                  <input
                    type="checkbox"
                    id="reminder"
                    checked={form.reminderEnabled}
                    className="w-4 h-4"
                    onChange={(e) => setForm({ ...form, reminderEnabled: e.target.checked })}
                  />
                  <label htmlFor="reminder" className="whitespace-nowrap">Remind me</label>

                  {form.reminderEnabled && (
                    <select
                      className="ml-5 border rounded px-2 py-1 text-sm"
                      value={form.reminderInterval || '60'}
                      onChange={handleChange}
                      name="reminderInterval"
                    >
                      <option value="5">5 minutes before</option>
                      <option value="15">15 minutes before</option>
                      <option value="30">30 minutes before</option>
                      <option value="60">1 hour before</option>
                      <option value="1440">1 day before</option>
                    </select>
                  )}
                </div>
              </div>


              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Notes (optional)</label>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  rows="3"
                  className="w-full p-2.5 border rounded-lg"
                />
              </div>

              {/* Submit */}
              <div className="md:col-span-2">
                <button
                  type="submit"
                  className="px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
                >
                  Confirm Appointment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && editingEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-xl p-6 relative">
            {/* Close Button */}
            <button
              onClick={() => setShowEditModal(false)}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-800"
            >
              ✕
            </button>

            <h3 className="text-xl font-bold mb-4">Edit Appointment</h3>

            <form className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* Title */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Title</label>
                <input
                  type="text"
                  name="title"
                  value={editingEvent.event_title}
                  onChange={(e) =>
                    setEditingEvent({ ...editingEvent, event_title: e.target.value })
                  }
                  className="w-full p-2.5 border rounded-lg"
                  required
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  name="type"
                  value={editingEvent.type}
                  onChange={(e) =>
                    setEditingEvent({ ...editingEvent, type: e.target.value })
                  }
                  className="w-full p-2.5 border rounded-lg"
                  required
                >
                  <option value="">Select Type</option>
                  <option value="Doctor">Doctor</option>
                  <option value="Lab Test">Lab Test</option>
                  <option value="Follow-Up">Follow-Up</option>
                  <option value="Transportation">Transportation</option>
                </select>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium mb-1">Location (optional)</label>
                <input
                  type="text"
                  name="location"
                  value={editingEvent.location || ""}
                  onChange={(e) =>
                    setEditingEvent({ ...editingEvent, location: e.target.value })
                  }
                  className="w-full p-2.5 border rounded-lg"
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <input
                  type="date"
                  name="date"
                  value={
                    editingEvent.date
                      ? new Date(editingEvent.date).toISOString().slice(0, 10)
                      : ""
                  }
                  onChange={(e) =>
                    setEditingEvent({ ...editingEvent, date: e.target.value })
                  }
                  className="w-full p-2.5 border rounded-lg"
                  required
                />
              </div>

              {/* Time */}
              <div className="flex gap-4 md:col-span-2">
                <div className="w-1/2">
                  <label className="block text-sm font-medium mb-1">Start Time</label>
                  <input
                    type="time"
                    name="start_time"
                    value={
                      editingEvent.start_time &&
                        !isNaN(new Date(editingEvent.start_time))
                        ? new Date(editingEvent.start_time).toTimeString().slice(0, 5)
                        : ""
                    }
                    onChange={(e) =>
                      setEditingEvent({
                        ...editingEvent,
                        start_time: `${editingEvent.date.slice(0, 10)} ${e.target.value}:00`,
                      })
                    }
                    className="w-full p-2.5 border rounded-lg"
                    required
                  />
                </div>
                <div className="w-1/2">
                  <label className="block text-sm font-medium mb-1">End Time (optional)</label>
                  <input
                    type="time"
                    name="end_time"
                    value={
                      editingEvent.end_time &&
                        !isNaN(new Date(editingEvent.end_time))
                        ? new Date(editingEvent.end_time).toTimeString().slice(0, 5)
                        : ""
                    }
                    onChange={(e) =>
                      setEditingEvent({
                        ...editingEvent,
                        end_time: `${editingEvent.date.slice(0, 10)} ${e.target.value}:00`,
                      })
                    }
                    className="w-full p-2.5 border rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Reminder</label>
                <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                  <input
                    type="checkbox"
                    id="reminder"
                    checked={Boolean(editingEvent.reminder_enabled)}
                    name="reminder_enabled"
                    className="w-4 h-4"
                    onChange={(e) =>
                      setEditingEvent({
                        ...editingEvent,
                        reminder_enabled: e.target.checked,
                      })
                    }
                  />
                  <label htmlFor="reminder" className="whitespace-nowrap">
                    Remind me
                  </label>

                  {Boolean(editingEvent.reminder_enabled) && (
                    <select
                      className="ml-5 border rounded px-2 py-1 text-sm"
                      value={
                        editingEvent.reminder_interval || '60'
                      }
                      onChange={(e) =>
                        setEditingEvent({
                          ...editingEvent,
                          reminder_interval: parseInt(e.target.value),
                        })
                      }
                      name="reminder_interval"
                    >
                      <option value="5">5 minutes before</option>
                      <option value="15">15 minutes before</option>
                      <option value="30">30 minutes before</option>
                      <option value="60">1 hour before</option>
                      <option value="1440">1 day before</option>
                    </select>
                  )}
                </div>
              </div>


              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Notes (optional)</label>
                <textarea
                  name="notes"
                  value={editingEvent.notes || ""}
                  onChange={(e) =>
                    setEditingEvent({ ...editingEvent, notes: e.target.value })
                  }
                  rows="3"
                  className="w-full p-2.5 border rounded-lg"
                />
              </div>

              <div className="flex items-center justify-between md:col-span-2">
                <button
                  className="text-white bg-red-600 px-4 py-2 rounded hover:bg-red-700"
                  onClick={async () => {
                    if (window.confirm("Delete this appointment?")) {
                      await deleteEvent(editingEvent.id);
                      const userId = localStorage.getItem("userId");
                      const updated = await fetchUserEvents(userId);
                      const data = await fetchUpcomingEvents(userId);
                      setAppointments(data);
                      const formatted = updated.map((ev) => ({
                        id: ev.id,
                        title: ev.event_title,
                        start: ev.start_time,
                        end: ev.end_time,
                        extendedProps: {
                          location: ev.location,
                          notes: ev.notes,
                          type: ev.type,
                          date: ev.date,
                        },
                      }));
                      setEvents(formatted);
                      setShowEditModal(false);
                    }
                  }}
                >
                  Delete
                </button>
                <button
                  className="text-white bg-blue-600 px-4 py-2 rounded hover:bg-blue-700"
                  onClick={async () => {
                    const dateStr = toMySQLDate(editingEvent.date);
                    const startDateTime = combineDateAndTime(dateStr, editingEvent.start_time);

                    let remind_at = null;

                    if (editingEvent.reminder_enabled && editingEvent.reminder_interval && startDateTime) {
                      const reminderTime = new Date(startDateTime);
                      reminderTime.setMinutes(reminderTime.getMinutes() - editingEvent.reminder_interval);
                      remind_at = formatToMySQLDatetime(reminderTime);
                    }

                    const eventToUpdate = {
                      ...editingEvent,
                      start_time: startDateTime,
                      end_time: editingEvent.end_time ? combineDateAndTime(dateStr, editingEvent.end_time) : null,
                      date: dateStr,
                      notes: editingEvent.notes || null,
                      remind_at: remind_at || null,
                      reminder_enabled: editingEvent.reminder_enabled || false,
                    };

                    await updateEvent(editingEvent.id, eventToUpdate);

                    const userId = localStorage.getItem("userId");

                    const updated = await fetchUserEvents(userId);
                    const data = await fetchUpcomingEvents(userId);
                    setAppointments(data);
                    const formatted = updated.map((ev) => ({
                      id: ev.id,
                      title: ev.event_title,
                      start: ev.start_time,
                      end: ev.end_time,
                      extendedProps: {
                        location: ev.location,
                        notes: ev.notes,
                        type: ev.type,
                        date: ev.date,
                        reminder_interval: ev.reminder_interval,
                      },
                    }));
                    setEvents(formatted);

                    setShowEditModal(false);
                  }}
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDetailsModal && selectedEvent && (
        <AppointmentDetailsModal
          selectedEvent={selectedEvent}
          onClose={() => setShowDetailsModal(false)}
          onEdit={handleEditClick}
        />
      )}

    </div>
  );
}
